import { Button, Checkbox } from '@wfp/react';
import { tooltipStyle } from '@wfp/react';
import { Tooltip } from 'acr_ui/sources/components/Tooltip';
import { isEmpty } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import ConfirmationModal from 'src/components/ConfirmationModal';
import { resolveCrfByPeriod } from 'src/crf';
import { resetReportsList } from 'src/redux/api/actions';
import { apiCallAsync } from 'src/redux/api/saga';
import { getPublicationListRequest } from 'src/redux/publicationStatus/actions';
import {
  getLocalStorageItem,
  setLocalStorageItem,
  showNotification,
} from 'src/utils';
import { Header, ReportData } from '../utils/reportsUtils';

interface PublicationActionManagerProps {
  data?: ReportData[];
  currentPeriod?: string;
  headers?: Header[];
}

interface ActionError {
  [key: string]: string;
}

interface ActionStatusResponse {
  processing?: boolean | null;
  errors?: ActionError[];
}

const ActionErrorsMsg: React.FC<{
  errors: ActionError[];
  publicationData: ReportData[];
}> = ({ errors, publicationData }) => {
  return (
    <>
      {errors.map(error => {
        const objKey = Object.keys(error);
        const reportInfo = publicationData.find(
          item => item.pk === parseInt(objKey?.[0] || '0'),
        );

        return (
          <div key={objKey?.[0]}>
            <li>
              {reportInfo?.project_code}: {error[parseInt(objKey?.[0] || '0')]}
            </li>
            <br />
          </div>
        );
      })}
    </>
  );
};

const PublicationActionManager: React.FC<PublicationActionManagerProps> = ({
  data = [],
  currentPeriod,
  headers = [],
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [readyProjectIds, setReadyProjectIds] = useState<string[]>([]);
  const [checkActionStatus, setCheckActionStatus] = useState<boolean>(false);

  const crf = resolveCrfByPeriod(currentPeriod);

  const publicationData = useMemo(() => {
    return data?.map(reportRow => {
      const actionStatus = crf.calcStatus(reportRow);
      const { current_status } = reportRow;

      const currentStatus = headers
        .find(item => item.key === current_status)
        ?.title.toLowerCase();

      return {
        ...reportRow,
        current_status: currentStatus,
        merge_status: actionStatus,
      };
    });
  }, [data, headers, crf]);

  const publicationDataRef = useRef(publicationData);
  useEffect(() => {
    publicationDataRef.current = publicationData;
  }, [publicationData]);

  const actionReadyFiles = useMemo(() => {
    return publicationData
      .filter(({ merge_status }) => merge_status === 'Ready')
      .map(item => item.project_code)
      .filter((code): code is string => !!code);
  }, [publicationData]);

  const toggleReadyProjectId = useCallback(
    (projectId: string) => {
      const projectIdKey = readyProjectIds.indexOf(projectId);
      const readyProjectsIdsClone = [...readyProjectIds];

      if (projectIdKey === -1) {
        readyProjectsIdsClone.push(projectId);
      } else {
        readyProjectsIdsClone.splice(projectIdKey, 1);
      }

      setReadyProjectIds(readyProjectsIdsClone);
    },
    [readyProjectIds, setReadyProjectIds],
  );

  const handleRefreshActionStatusCheck = useCallback(async () => {
    const url = crf.statusUrl();

    try {
      const response = await apiCallAsync(url, null, 'get');
      const { processing = null, errors = [] }: ActionStatusResponse =
        response?.data || {};
      if (processing) return;

      if (isEmpty(errors)) {
        showNotification(crf.actionCompletedMessage, 'success', {});
        dispatch(resetReportsList());
        dispatch(
          getPublicationListRequest({
            period: currentPeriod
              ? currentPeriod
              : location.pathname.replace(/[^0-9]/g, ''),
          }),
        );
      } else {
        showNotification(
          <ActionErrorsMsg
            errors={errors}
            publicationData={publicationDataRef.current}
          />,
          'error',
          {},
        );
      }
      setCheckActionStatus(false);
      setLocalStorageItem(crf.pendingFlagKey, '-', false);
    } catch (error) {
      console.log(error);
    }
  }, [currentPeriod, location.pathname, dispatch, crf]);

  useEffect(() => {
    setCheckActionStatus(!!getLocalStorageItem(crf.pendingFlagKey));
  }, [crf.pendingFlagKey]);

  useEffect(() => {
    const timeoutID: NodeJS.Timeout | null = checkActionStatus
      ? setInterval(() => {
          handleRefreshActionStatusCheck();
        }, 3000)
      : null;
    if (!checkActionStatus && timeoutID) {
      clearInterval(timeoutID);
    }

    return () => {
      if (timeoutID) clearInterval(timeoutID);
    };
  }, [checkActionStatus, handleRefreshActionStatusCheck]);

  const startAction = useCallback(async () => {
    const url = crf.startActionUrl();

    const pkList = publicationData
      .filter(
        item =>
          item.project_code && readyProjectIds.includes(item.project_code),
      )
      .map(item => item.pk)
      .filter((pk): pk is number => !!pk);

    try {
      const response = await apiCallAsync(url, { pk_list: pkList }, 'post');

      if (response?.data !== crf.actionStartedResponse)
        throw new Error(`Unexpected response: ${response?.data}`);

      showNotification(crf.actionStartedMessage, 'success', {});
      setLocalStorageItem(crf.pendingFlagKey, '+', false);
      setCheckActionStatus(true);
    } catch (error) {
      console.error(error);
      showNotification(
        `Error while starting ${crf.actionLabel.toLowerCase()}. Please try later`,
        'error',
        {},
      );
    }

    setConfirmationModal(false);
  }, [publicationData, readyProjectIds, crf]);

  const handleActionReady = useCallback(async () => {
    const url = crf.statusUrl();

    try {
      const response = await apiCallAsync(url, null, 'get');
      const { processing = null }: ActionStatusResponse = response?.data || {};

      if (processing) {
        showNotification('Another operation is in progress', 'error', {});
        return;
      }

      setConfirmationModal(true);
    } catch (error) {
      console.error(error);
      showNotification(
        'Error on checking the operation status. Please try later',
        'error',
        {},
      );
    }
  }, [crf]);

  const isDisabled = actionReadyFiles.length === 0 || checkActionStatus;

  const actionButton = (
    <Button
      disabled={isDisabled}
      onClick={handleActionReady}
      data-test-id={crf.actionTestId}
    >
      {crf.actionLabel}
    </Button>
  );

  return (
    <>
      {isDisabled ? (
        <Tooltip
          title="No records available"
          trigger="mouseenter"
          {...tooltipStyle}
          theme="light"
          position="bottom"
          disabled={actionReadyFiles.length > 0}
        >
          {actionButton}
        </Tooltip>
      ) : (
        <div>{actionButton}</div>
      )}

      <ConfirmationModal
        open={confirmationModal}
        onSubmit={startAction}
        onClose={() => setConfirmationModal(false)}
        heading={crf.confirmationHeading}
        primaryButtonDisabled={!readyProjectIds?.length}
        primaryButtonText="Confirm"
        content={
          <div className="merge-ready-modal">
            {crf.confirmationIntro} <br />
            <br />
            {actionReadyFiles.map(item => (
              <Checkbox
                id={item}
                key={item}
                labelText={item}
                onChange={() => toggleReadyProjectId(item)}
                checked={readyProjectIds.includes(item)}
              />
            ))}
            <br />
            {crf.confirmationQuestion}
          </div>
        }
      />
    </>
  );
};

export default PublicationActionManager;
